import { Box, Stack, Typography } from '@mui/material';
import { DataTable, Panel, StatusChip, type DataTableColumn } from '../../../../ui-kit';
import type { CloseOutRule, ReportPayload } from '../../../../types/reportDashboard';

const CHECK = {
  tight: { tone: 'warning' as const, label: 'Too tight' },
  loose: { tone: 'neutral' as const, label: 'Longer than needed' },
  ok: { tone: 'good' as const, label: 'Matches the cases' },
  unknown: { tone: 'neutral' as const, label: 'Too few cases' },
};

const whole = (v: number) => v.toLocaleString('en-US');

/**
 * The carriers' days, why each one is what it is, and how long that carrier
 * really takes — read from its own decisions, so the rule and the record sit
 * side by side. A rule that no longer matches its cases says so.
 *
 * Read only: the days live in the board's spec, where Klaris staff change them.
 */
export default function CloseOutPanel({ payload, loading }: { payload: ReportPayload; loading?: boolean }) {
  const { title, subtitle, carriers, others, default: fallback } = payload.close_out;

  const columns: DataTableColumn<CloseOutRule>[] = [
    { key: 'carrier', header: 'Carrier', width: 180, sticky: true, render: (r) => r.carrier },
    {
      key: 'days',
      header: 'Cutoff days applied',
      numeric: true,
      width: 150,
      render: (r) => <Box component="span" sx={{ fontWeight: 600 }}>{r.days}</Box>,
    },
    {
      key: 'after',
      header: 'Decisions after the cutoff',
      numeric: true,
      width: 190,
      render: (r) => (r.decided_after_pct === null ? '—' : (
        <Stack spacing={0} alignItems="flex-end">
          <span>{whole(r.decided_after)}</span>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {r.decided_after_pct.toFixed(1)}% of {whole(r.decided)}
          </Typography>
        </Stack>
      )),
    },
    {
      key: 'check',
      header: 'The cases say',
      width: 190,
      render: (r) => <StatusChip tone={CHECK[r.check].tone} label={CHECK[r.check].label} />,
    },
    {
      key: 'says',
      header: 'What this means',
      render: (r) => (
        <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>{r.says}</Typography>
      ),
    },
  ];

  // Two groups, and only real carriers in them: the ones we set a number for,
  // and the ones left on the fallback.
  const groups = [
    { key: 'rules', title: 'Carriers with a cutoff of their own', rows: carriers },
    ...(others.length
      ? [{ key: 'others', title: `Carriers on the fallback of ${fallback.days} days`, rows: others }]
      : []),
  ];

  const table = (fill: boolean) => (
    <DataTable
      ariaLabel={title}
      columns={columns}
      groups={groups}
      getRowKey={(r) => r.carrier}
      collapsibleGroups={false}
      columnPicker={false}
      maxHeight={fill ? undefined : 520}
      fill={fill}
    />
  );

  return (
    <Panel
      title={title}
      subtitle={subtitle}
      info={
        <>
          How long a case can wait on a carrier before we stop counting on a decision, counted from the day we sent
          it. Past that day, decisions from that carrier almost never arrive, so a case coming up to it is flagged at
          risk. It does not make the case lost: only the client&apos;s own closed lost mark does that. A carrier we
          have not set a number for waits the fallback of {fallback.days} days.
          <Box component="span" sx={{ display: 'block', mt: 1.5 }}>
            &quot;Decisions after the cutoff&quot; counts the decisions that carrier sent us later than its cutoff
            day, read from the cases of the last three years that have had time to settle. Those are cases we would
            have flagged at risk while the carrier was still working on them. &quot;The cases say&quot; is the
            check: a cutoff that cuts off more than 2% of a carrier&apos;s decisions is too tight, and one well past
            the slowest decision flags a case later than it could.
          </Box>
        </>
      }
      loading={loading}
      loadingHeight={320}
      collapsible
      flush
      expanded={{ content: table(true) }}
    >
      {table(false)}
    </Panel>
  );
}
